import { useRef, useState } from 'react';
import { GridList, GridListItem } from 'react-aria-components';
import { Trans, useLingui } from '@lingui/react/macro';

import { ArticlePhotoGroup } from '@/components/ArticlePhotoGroup';
import Loader from '@/components/Loader';
import { TableQueryWrapper } from '@/components/TableQueryWrapper';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Checkbox from '@/design_system/Checkbox';
import Pagination from '@/design_system/Pagination';
import { PaginationFooter } from '@/design_system/Pagination/Pagination';
import Stack from '@/design_system/Stack';
import { SortableColumnHeader } from '@/design_system/Table/SortableColumnHeader';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import IconNotification from '@/icons/Notification.svg';
import { isInDraftStep, RequestWithRelationsFromRequestsList } from '@/models/request';
import { isExternalWorkshopUser } from '@/models/user';
import { RequestsEmptyState } from '@/routes/Requests/components/RequestsEmptyState';
import { RequestsNoResults } from '@/routes/Requests/components/RequestsNoResults';
import { useToggleSubscription } from '@/routes/Requests/Request/components/RequestHeader/SubscriptionButton';
import { useCurrentSession, useFlags } from '@/services/auth';
import { createBEMClasses } from '@/utils/classname';
import { formatDate } from '@/utils/date';
import useViewPort from '@/utils/useViewport';

import { useFilters } from '../Filters/FiltersState';
import { handleChangeSort } from '../Filters/utils';
import { resolveRequestCardPress } from '../hooks/resolve-request-card-press';
import { useLongPress } from '../hooks/useLongPress';
import { RequestSelection } from '../hooks/useRequestSelection';

import { RequestDueAtCell } from './components/RequestDueAtCell';
import {
  ArticlesCardItem,
  getAppliedRequestsSearch,
  RequestName,
  RequestNameCardItem,
  RequestReference,
  REQUESTS_PER_PAGE,
} from './common';

import './RequestsTab.css';

const { block, element } = createBEMClasses('requests-tab');

export type ColumnAndCell = {
  column: {
    name: string;
    width: string | undefined;
    sortId?: 'reference' | 'statusDueAt' | 'dueAt' | 'referencedAt';
    /**
     * Rendered instead of `name` in the header. `name` is then used as the column accessible label.
     */
    content?: React.ReactNode;
    className?: string;
  };
  cell: {
    content: (request: RequestWithRelationsFromRequestsList) => React.ReactNode;
    isLink?: boolean;
    className?: string;
    style?: React.CSSProperties;
  };
  hide?: boolean;
};

const getRequestLink = (request: RequestWithRelationsFromRequestsList) =>
  isInDraftStep(request) ? `/requests/new/${request.id}` : `/requests/${request.id}`;

export const RequestsTab = ({
  requests,
  count,
  isLoading,
  columnsAndCellsForTab,
  cardElementsForTab,
  isError,
  selection,
}: {
  requests: RequestWithRelationsFromRequestsList[];
  count?: number;
  isLoading: boolean;
  columnsAndCellsForTab: ColumnAndCell[];
  cardElementsForTab: (request: RequestWithRelationsFromRequestsList) => React.ReactElement;
  isError: boolean;
  selection: RequestSelection;
}) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();
  const { currentSession } = useCurrentSession();
  const { flags } = useFlags();
  const { activeFiltersCount, search, page, sort, tab } = useFilters();

  const isBulkActionsEnabled = !!flags['enable-bulk-actions'];

  const hasActiveFilters = activeFiltersCount > 0;
  const isSelectionActive = selection.selectedIds.size > 0;

  // A long press toggles selection at 400ms. Touch browsers then synthesize a
  // click / `onAction` after pointerup. That single action on the long-pressed
  // card is ignored, so the release does not toggle again or open the request.
  // Any new press clears the guard, so quick taps on other cards are not blocked.
  // It is a state so the long-pressed card drops its link until the release is consumed.
  const [longPressedRequestId, setLongPressedRequestId] = useState<string | null>(null);

  const handleCardPointerDown = () => {
    setLongPressedRequestId(null);
  };

  const handleCardLongPress = (requestId: string) => {
    setLongPressedRequestId(requestId);
    selection.toggle(requestId);
  };

  const selectionColumnAndCell: ColumnAndCell = {
    hide: !isBulkActionsEnabled,
    column: {
      name: t({ id: 'requests.table.column.selection.title', message: 'Selection' }),
      width: isBulkActionsEnabled ? '76px' : undefined,
      className: element('selection-cell'),
      content: (
        <SelectionControl>
          <Checkbox
            aria-label={
              selection.isAllSelected
                ? t({
                    id: 'requests.table.selection.unselect-all',
                    message: 'Unselect all requests on this page',
                  })
                : t({
                    id: 'requests.table.selection.select-all',
                    message: 'Select all requests on this page',
                  })
            }
            isSelected={selection.isAllSelected}
            isIndeterminate={selection.isIndeterminate}
            isDisabled={!requests.length}
            onChange={(isSelected) =>
              isSelected ? selection.selectAll(requests.map(({ id }) => id)) : selection.clear()
            }
          />
          <span className={element('follow-spacer')} aria-hidden />
        </SelectionControl>
      ),
    },
    cell: {
      content: (request: RequestWithRelationsFromRequestsList) => (
        <RequestSelectionCheckbox request={request} selection={selection} />
      ),
      className: element('selection-cell'),
    },
  };

  const commonColumnsAndCells: ColumnAndCell[] = [
    selectionColumnAndCell,
    {
      column: {
        name: t({ id: 'requests.table.column.reference.title', message: 'Reference' }),
        width: 'minmax(104px, 150px)',
        sortId: 'reference',
      },
      cell: {
        content: (request: RequestWithRelationsFromRequestsList) => (
          <RequestReference request={request} />
        ),
        isLink: true,
      },
    },
    {
      column: {
        name: t({ id: 'requests.table.column.applicant.title', message: 'Applicant' }),
        width: '1.5fr',
      },
      cell: {
        content: (request: RequestWithRelationsFromRequestsList) => (
          <RequestName request={request} />
        ),
        className: 'sentry-mask',
      },
    },
    {
      hide: isExternalWorkshopUser(currentSession),
      column: {
        name: t({
          id: 'requests.table.column.request-creation-date.title',
          message: 'Creation date',
        }),
        width: !isExternalWorkshopUser(currentSession) ? '1fr' : undefined,
        sortId: 'referencedAt',
      },
      cell: {
        content: (request: RequestWithRelationsFromRequestsList) =>
          formatDate(new Date(request.referencedAt ?? request.createdAt), {
            dateStyle: 'medium',
            year: undefined,
          }),
      },
    },
    {
      column: {
        name: t({ id: 'requests.table.column.items.title', message: 'Items' }),
        width: '1fr',
      },
      cell: {
        content: (request: RequestWithRelationsFromRequestsList) => (
          <ArticlePhotoGroup articles={request.allArticles} mode="task" />
        ),
      },
    },
  ];

  const columnsAndCells = [...commonColumnsAndCells, ...columnsAndCellsForTab];
  const columnsWidths = columnsAndCells.map((columnCell) => columnCell.column.width);

  return (
    <Stack gap="1rem" className={block()}>
      <TableQueryWrapper isError={isError}>
        <>
          {!isMobile && (
            <Table
              ariaLabel={t({ id: 'requests.table.label', message: 'Requests' })}
              columnWidths={columnsWidths}
            >
              <Header>
                <Row>
                  {columnsAndCells.map((columnCell) =>
                    !columnCell.hide ? (
                      columnCell.column.sortId ? (
                        <SortableColumnHeader
                          key={columnCell.column.name}
                          isActive={sort.sort[tab.tab].field === columnCell.column.sortId}
                          onChange={() => {
                            handleChangeSort(tab.tab, columnCell.column.sortId!, sort);
                          }}
                          sortOrder={sort.sort[tab.tab].order}
                        >
                          {columnCell.column.name}
                        </SortableColumnHeader>
                      ) : (
                        <Column
                          key={columnCell.column.name}
                          ariaLabel={columnCell.column.content ? columnCell.column.name : undefined}
                          className={columnCell.column.className}
                        >
                          {columnCell.column.content ?? columnCell.column.name}
                        </Column>
                      )
                    ) : null
                  )}
                </Row>
              </Header>

              <Body>
                {!isLoading &&
                  requests?.map((request) => (
                    <RequestRow
                      key={request.id}
                      request={request}
                      columnsAndCells={columnsAndCells}
                      isSelected={selection.selectedIds.has(request.id)}
                    />
                  ))}
              </Body>

              {requests?.length > 0 && !isLoading && (
                <PaginationFooter
                  page={page.page}
                  itemsPerPage={REQUESTS_PER_PAGE}
                  count={count}
                  onPageChange={page.setPage}
                />
              )}
            </Table>
          )}

          {isMobile && (
            <>
              {/*
               * Gmail-style: RAC `selectionBehavior="replace"` selects on tap (mouse and many
               * mobile browsers). Selection is ours: tap opens when empty, tap/long-press
               * toggle while at least one card is selected.
               */}
              <GridList
                aria-label={t({ id: 'requests.table.label', message: 'Requests' })}
                className={element('cards')}
                onAction={(key) => {
                  const isLongPressSuppressed = longPressedRequestId === String(key);

                  if (isLongPressSuppressed) {
                    setLongPressedRequestId(null);
                  }

                  const action = resolveRequestCardPress({
                    isSelectionActive: selection.selectedIds.size > 0,
                    isLongPressSuppressed,
                  });

                  // 'open' is handled by the card `href`
                  if (action === 'toggle') {
                    selection.toggle(String(key));
                  }
                }}
              >
                {!isLoading &&
                  requests?.map((request) => (
                    <RequestCard
                      key={request.id}
                      request={request}
                      cardElements={cardElementsForTab}
                      isSelected={selection.selectedIds.has(request.id)}
                      isSelectionActive={isSelectionActive}
                      isLinkSuppressed={longPressedRequestId === request.id}
                      onPointerDown={handleCardPointerDown}
                      onLongPress={isBulkActionsEnabled ? handleCardLongPress : undefined}
                    />
                  ))}
              </GridList>
              {requests?.length > 0 && !isLoading && (
                <Pagination
                  page={page.page}
                  itemsPerPage={REQUESTS_PER_PAGE}
                  count={count}
                  onPageChange={page.setPage}
                />
              )}
            </>
          )}

          {isLoading ? (
            <Stack alignItems="center" justifyContent="center" style={{ flex: 1 }}>
              <Loader
                style={{
                  fontSize: '40px',
                }}
              />
            </Stack>
          ) : (
            !requests?.length &&
            (getAppliedRequestsSearch(search.debouncedSearch) || hasActiveFilters ? (
              <RequestsNoResults />
            ) : (
              <RequestsEmptyState
                title={
                  tab.tab === 'to-do' ? (
                    <Trans id="requests.empty-state.all-caught-up-title">
                      You're all caught up!
                    </Trans>
                  ) : null
                }
                subtitle={
                  tab.tab === 'to-do' ? (
                    <Trans id="requests.empty-state.all-caught-up-subtitle">
                      You don't have any requests to deal with at the moment
                    </Trans>
                  ) : (
                    <Trans id="requests.empty-state.no-request-subtitle">
                      No requests to display
                    </Trans>
                  )
                }
              />
            ))
          )}
        </>
      </TableQueryWrapper>
    </Stack>
  );
};

const stopRowNavigation = (event: React.SyntheticEvent) => {
  event.preventDefault();
  event.stopPropagation();
};

/**
 * Rows are links (`Row to={…}`) and the Table only skips the navigation for `BUTTON` targets or
 * already handled events. This wrapper stops the click before it reaches the row, so that ticking
 * a checkbox or toggling follow doesn't open the request.
 */
const SelectionControl = ({ children }: { children: React.ReactNode }) => (
  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
  <div
    className={element('selection-control')}
    onClick={stopRowNavigation}
    onPointerDown={stopRowNavigation}
  >
    {children}
  </div>
);

const RequestSelectionCheckbox = ({
  request,
  selection,
}: {
  request: RequestWithRelationsFromRequestsList;
  selection: RequestSelection;
}) => {
  const { t } = useLingui();

  const isSelected = selection.selectedIds.has(request.id);
  const reference = request.reference;

  // The checkbox `onChange` has no access to the click event: remember the modifiers beforehand.
  // Only ever set to true (a label click re-dispatches an unmodified click on the input), and
  // reset once consumed by `onChange`.
  const isRangeClickRef = useRef(false);

  return (
    <SelectionControl>
      {/* Capture the modifiers on the checkbox only, not on the sibling follow toggle */}
      <span
        className={element('checkbox')}
        onClickCapture={(event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey) {
            isRangeClickRef.current = true;
          }
        }}
      >
        <Checkbox
          aria-label={
            isSelected
              ? t({
                  id: 'requests.table.selection.unselect',
                  message: `Unselect request ${reference}`,
                })
              : t({
                  id: 'requests.table.selection.select',
                  message: `Select request ${reference}`,
                })
          }
          isSelected={isSelected}
          onChange={() => {
            if (isRangeClickRef.current) {
              selection.toggleRange(request.id);
            } else {
              selection.toggle(request.id);
            }

            isRangeClickRef.current = false;
          }}
        />
      </span>
      <RequestFollowToggle request={request} />
    </SelectionControl>
  );
};

const RequestFollowToggle = ({
  request,
  alwaysVisible = false,
}: {
  request: RequestWithRelationsFromRequestsList;
  alwaysVisible?: boolean;
}) => {
  const { t } = useLingui();
  const { toggleSubscription, isSubscribeToRequestPending, isUnsubscribeFromRequestPending } =
    useToggleSubscription({ request });

  const hasSubscribed = !!request.hasSubscribed;
  const isPending = isSubscribeToRequestPending || isUnsubscribeFromRequestPending;

  const label = hasSubscribed
    ? t({ id: 'requests.edit.actions.unsubscribe', message: 'Unsubscribe from notifications' })
    : t({ id: 'requests.edit.actions.subscribe', message: 'Subscribe to notifications' });

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={element('follow-control')}
      onClick={stopRowNavigation}
      onPointerDown={stopRowNavigation}
    >
      <Button
        className={element('follow', {
          subscribed: hasSubscribed,
          'always-visible': alwaysVisible,
        })}
        variant="style-less"
        size="small"
        iconOnly
        ariaLabel={label}
        tooltip={label}
        disabled={isPending}
        onPress={toggleSubscription}
      >
        <IconNotification
          className={hasSubscribed ? 'color-primary-700' : ''}
          plain={hasSubscribed}
        />
      </Button>
    </div>
  );
};

const RequestRow = ({
  request,
  columnsAndCells,
  isSelected,
}: {
  request: RequestWithRelationsFromRequestsList;
  columnsAndCells: ColumnAndCell[];
  isSelected: boolean;
}) => {
  return (
    <Row
      to={getRequestLink(request)}
      className={isSelected ? element('row', { selected: true }) : undefined}
    >
      {columnsAndCells.map((columnCell) =>
        !columnCell.hide ? (
          <Cell
            key={columnCell.column.name}
            isLink={columnCell.cell.isLink}
            className={columnCell.cell.className}
            style={columnCell.cell.style}
          >
            {columnCell.cell.content(request)}
          </Cell>
        ) : null
      )}
    </Row>
  );
};

const RequestCard = ({
  request,
  cardElements,
  isSelected,
  isSelectionActive,
  isLinkSuppressed,
  onPointerDown,
  onLongPress,
}: {
  request: RequestWithRelationsFromRequestsList;
  cardElements: (request: RequestWithRelationsFromRequestsList) => React.ReactElement;
  isSelected: boolean;
  isSelectionActive: boolean;
  /**
   * The card was just long-pressed: its release must not open the request.
   */
  isLinkSuppressed: boolean;
  onPointerDown: () => void;
  /**
   * Long press enters the selection mode. Without it (bulk actions disabled), the card behaves as a
   * plain link and doesn't show the follow bell.
   */
  onLongPress?: (requestId: string) => void;
}) => {
  const { currentSession } = useCurrentSession();
  const longPress = useLongPress(() => onLongPress?.(request.id));
  const isSelectable = !!onLongPress;

  return (
    <GridListItem
      id={request.id}
      // A real link while a tap opens the request, so link semantics and browser link actions are kept
      href={isSelectionActive || isLinkSuppressed ? undefined : getRequestLink(request)}
      textValue={request.reference}
      className={element('card', { selected: isSelected, selectable: isSelectable })}
      data-selected={isSelected || undefined}
      aria-selected={isSelected}
      onPointerDown={(event) => {
        onPointerDown();

        if (isSelectable) {
          longPress.onPointerDown(event);
        }
      }}
      onPointerMove={isSelectable ? longPress.onPointerMove : undefined}
      onPointerUp={isSelectable ? longPress.onPointerUp : undefined}
      onPointerCancel={isSelectable ? longPress.onPointerCancel : undefined}
      onLostPointerCapture={isSelectable ? longPress.onLostPointerCapture : undefined}
      onClick={(event) => {
        // In selection mode, or just after a long press, never follow a leftover click as a link.
        if (isSelectionActive || event.defaultPrevented) {
          event.preventDefault();
        }
      }}
      onContextMenu={(event) => {
        if (isSelectable) {
          event.preventDefault();
        }
      }}
    >
      <Box padding="16px" role="row">
        <Stack row justifyContent="space-between">
          <Stack row gap="0.5rem" alignItems="center" style={{ marginBottom: '0.125rem' }}>
            <RequestReference request={request} />
            {isSelectable && <RequestFollowToggle request={request} alwaysVisible />}
          </Stack>

          {!isExternalWorkshopUser(currentSession) && (
            <RequestDueAtCell request={request} variant="card" />
          )}
        </Stack>

        <RequestNameCardItem request={request} />

        <Stack row gap="0.5rem" alignItems="center" style={{ marginTop: '0.75rem' }}>
          <ArticlesCardItem request={request} />
          {cardElements(request)}
        </Stack>
      </Box>
    </GridListItem>
  );
};
